import React, { useEffect, useState } from 'react';
import { api } from '@/api/client';
import ConfirmDestructiveDialog from '@/components/ConfirmDestructiveDialog';
import { shortId } from '@/utils/jobStatus';

type Props = {
  jobId: string;
  onClose: () => void;
  onDone: (message: string) => void;
};

export default function ClearJobActivityConfirmModal({ jobId, onClose, onDone }: Props) {
  const [preview, setPreview] = useState<{ total: number } | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoadingPreview(true);
    void api.jobs
      .activityPreview(jobId)
      .then((result) => {
        if (!cancelled) setPreview(result);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load preview');
      })
      .finally(() => {
        if (!cancelled) setLoadingPreview(false);
      });
    return () => {
      cancelled = true;
    };
  }, [jobId]);

  async function confirm() {
    setSubmitting(true);
    setError('');
    try {
      const result = await api.jobs.clearActivity(jobId);
      onDone(
        `Cleared ${result.total} activity record(s) and removed job ${shortId(jobId)} from operational views`,
      );
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Clear activity failed');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <ConfirmDestructiveDialog
      title="Clear related activity?"
      onClose={onClose}
      closeOnBackdrop={!submitting}
      footer={
        <>
          <button type="button" className="btn btn-ghost" disabled={submitting} onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-danger"
            disabled={submitting || loadingPreview}
            onClick={() => void confirm()}
          >
            {submitting ? 'Clearing…' : 'Clear activity'}
          </button>
        </>
      }
    >
      {error && <div className="alert alert-error">{error}</div>}

      <p className="modal-lead">
        {loadingPreview ? (
          'Counting related activity…'
        ) : (
          <>
            Remove <strong>{preview?.total ?? 0}</strong> activity record(s) linked to job{' '}
            <strong>{shortId(jobId)}</strong>?
          </>
        )}
      </p>

      <p className="modal-hint">
        Removes deliveries, pipeline executions, and fetch runs for this job, then hides the job from Jobs,
        Activity, and dashboard views. Audit records are retained but no longer shown in normal operational
        lists.
      </p>
    </ConfirmDestructiveDialog>
  );
}
